/**
 * IdleModeView React component
 * 
 * @author    Maurizio Tidei, Jens Gruschel
 * @copyright © 2020 contexagon GmbH
 */


import React from 'react';
import PropTypes from 'prop-types';
import {patchServerUrl} from "../../../config";
import {getMediaUrl} from '../../../operations/utils';
import IdleModeElement from "./IdleModeElement";
import Fade from "@material-ui/core/Fade";


const screenRatio = 16 / 9;
const screenHeightVW = screenRatio * 100;


/**
 * IdleModeView
 *
 * @author Maurizio Tidei, Jens Gruschel
 */
class IdleModeView extends React.Component {

    kubusIndex = 0;
    duration = 0;
    presentationProgramFields = null;
    idleElement = null;

    visibleTime = 8; // seconds
    fadeTime = 1.5; // before and after visible
    blackTime = 0.5; // before and after fading

    totalKubus = 3;
    layouts = [[1,1,0], [1,0,1], [0,1,1], [0,0,1], [0,1,0], [1,0,0]]; // 1=element on upper screen, 0=lower
    layoutIndex = 0;

    state = {
        idleElementVisible: false
    };

    constructor(props) {
        super(props);

        // Set video index based on this cbox name number suffix (1 digit), 0 on missing suffix
        const suffix = props.boxName && props.boxName.charAt(props.boxName.length - 1);
        this.kubusIndex = isNaN(parseInt(suffix)) ? 0 : (parseInt(suffix) - 1) % this.totalKubus;
    }

    render() {

        const {screenIndex} = this.props;
        const topVW = screenIndex ? screenIndex * screenHeightVW : 0;

        const presentationProgram = this.props.presentationProgram || {};
        this.presentationProgramFields = presentationProgram.fields || {};

        const idleVideoItem = this.getSingleReferencedObject(this.presentationProgramFields.idleVideo, this.kubusIndex);
        this.duration = idleVideoItem && idleVideoItem.mediainfo && idleVideoItem.mediainfo.duration;
        const idleVideoUrl = getMediaUrl(idleVideoItem);
        //console.log("idle video info:", idleVideoItem, idleVideoUrl);

        const layout = this.layouts[this.layoutIndex];
        const boxElementLayout = layout[this.kubusIndex];
        let className = "upper";
        if(boxElementLayout === 0) {
            className = "bottom";
        }

        return <div style={{transform: "translate(0, " + -topVW + "vw)"}}>
            <div className="presentation-screen idle-screen">

                {idleVideoItem && <video
                    ref={function(ref) { this.video = ref }.bind(this)}
                    key={idleVideoUrl} autoPlay loop id="idleVideo" style={{
                    width: "100vw"
                }}>
                    <source src={patchServerUrl(idleVideoUrl)} type="video/mp4"/>
                </video>}
                {this.idleElement &&
                <div>
                    <IdleModeElement visible={this.state.idleElementVisible} fadeTime={this.fadeTime}
                                     idleModeElement={this.idleElement} className={className}
                                     findReferencedObjects={this.props.findReferencedObjects}/>
                </div>}
            </div>
        </div>;
    }

    componentDidMount() {
        if (this.video) {
            this.video.currentTime = this.getTimePassedSinceLastStartPoint();
            this.checkVideoTimeAndAdjustSpeed = this.checkVideoTimeAndAdjustSpeed.bind(this);
            this.getTimePassedSinceLastStartPoint = this.getTimePassedSinceLastStartPoint.bind(this);
            this.updateMainLayerElement = this.updateMainLayerElement.bind(this);
            this.interval = setInterval(() => {
                    this.checkVideoTimeAndAdjustSpeed();
                    this.updateMainLayerElement();
                }, 200)
        }
    }

    componentWillUnmount() {
        clearInterval(this.interval);
    }

    getTimePassedSinceLastStartPoint() {
        return Math.round(Date.now()) % Math.round(this.duration * 1000) / 1000;
    }

    checkVideoTimeAndAdjustSpeed() {

        if (!this.video) return;

        const tolerance = 0.01;
        const diff = this.video.currentTime - this.getTimePassedSinceLastStartPoint();

        if(Math.abs(diff) > 3) {
            // jump to position if difference is greater than 3 seconds (this should never happen, only if browser window gets into background)
            this.video.currentTime = this.getTimePassedSinceLastStartPoint();
        }
        else if(Math.abs(diff) > tolerance) {
            // speed up or slow down?
            const direction = Math.abs(diff) <= (this.duration / 2) ? diff > 0 ? -1 : 1 : diff > 0 ? 1 : -1;
            const minSpeedUp = 0.01;
            const maxSpeedUp = 0.1;
            const speedUpRate = Math.abs(diff/2) > minSpeedUp ? (Math.abs(diff/2) < maxSpeedUp ? 1 + Math.abs(diff/2) : 1 + maxSpeedUp) : 1 + minSpeedUp;
            const newPlaybackRate = direction === 1 ? 1 * speedUpRate : 1 / speedUpRate;
            this.video.playbackRate = newPlaybackRate;
        }
        else {
            this.video.playbackRate = 1;
        }

        // console.log("actualVideoTime|targetVideoTime|diff|playbackSpeed", this.video.currentTime, this.getTimePassedSinceLastStartPoint(), diff, this.video.playbackRate);
    }

    updateMainLayerElement() {

        const idleElements = this.presentationProgramFields.idleElements;
        if (!idleElements) return;

        const now = Date.now()/1000;

        // Calculate if idle mode element is visible and position
        const period = this.blackTime * 2 + this.visibleTime + this.fadeTime * 2;
        const timeInPeriod = now % period;
        const periodNumber = Math.floor(now / period);
        this.layoutIndex = periodNumber % this.layouts.length;

        // Select an idle mode element, based on timestamp and kubus index
        const numberOfIdleElements = idleElements.length;
        const idleElementIndex = (periodNumber * this.totalKubus + this.kubusIndex) % numberOfIdleElements;
        this.idleElement = idleElements[idleElementIndex];
        //console.log("idleElementIndex:", idleElementIndex, " timeInPeriod:", timeInPeriod, " periodNumber:", periodNumber);

        let idleElementVisible = false;
        if(timeInPeriod > this.blackTime && timeInPeriod < (this.visibleTime - this.blackTime - this.fadeTime)) {
            idleElementVisible = true;
        }

        if (this.state.idleElementVisible !== idleElementVisible) this.setState({idleElementVisible: idleElementVisible});
    }

    getSingleReferencedObject(field, index = 0) {
        if (!field || index < 0) return null;
        const items = this.props.findReferencedObjects(field);
        return items && items.length > index ? items[index] : null;
    }
}

IdleModeView.propTypes = {
    screenIndex: PropTypes.number,
    boxName: PropTypes.string,
    presentationProgram: PropTypes.object,
    findReferencedObjects: PropTypes.func.isRequired
};

export default IdleModeView;
